/**
 *
 * GSTR-9C New Fetched Summary Component
 *
 */

import React, { useState, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button, Tooltip } from "@material-ui/core";
import { GetApp as DownloadIcon } from "@material-ui/icons";
import BootstrapTable from "react-bootstrap-table-next";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import moment from "moment";
import { COMMON_MSGS } from "Constant/Messages";
import { errorToastMessage } from "utils/errorToast";
import {
  downloadFetchReport,
  fetchSummary,
  getFetchSubmitData,
  setSelectedFetch,
  setSelectedRowFetch,
} from "./actions";

const FetchedSummary = (props) => {
  const {
    selectedGstinList,
    selectedReturnPeriod,
    validateRequiredFields,
    getFetchData,
    clearAndRestartInterval,
  } = props;
  const dispatch = useDispatch();

  // Local state for loader management
  const [showLoader, setShowLoader] = useState(false);
  const loaderTimeoutRef = useRef(null);
  const intervalRef = useRef(null);

  // Redux selectors
  const { fetchTableData, loaderStatus, selectedFetch, selectedRowFetch } =
    useSelector((state) => state.gstr9CNewSaveReducer || {});

  // Helper function to count in-process files (following Fetch pattern)
  const countInProcessFiles = (files) =>
    files?.filter((item) => {
      const status = (item?.STATUS || item?.status)?.toUpperCase();
      // Consider null, undefined, or any status that's not COMPLETED/SUCCESS/FAILED/NOT-INITIATED as in-progress
      // Exclude "Not-Initiated" from triggering continuous polling
      return !status || !["COMPLETED", "SUCCESS", "FAILED", "NOT-INITIATED"].includes(status);
    })?.length;

  // Auto-refresh logic for in-process files (following Fetch pattern)
  const startAutoRefresh = async () => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Count in-process files
    let inProcessStatusCount = countInProcessFiles(fetchTableData);
    console.log("📊 FetchedSummary - In-process files count:", inProcessStatusCount);

    // Check for Not-Initiated status
    const hasNotInitiated = fetchTableData?.some(item => {
      const status = (item?.STATUS || item?.status)?.toUpperCase();
      return status === 'NOT-INITIATED';
    });

    if (hasNotInitiated) {
      console.log("⚠️ FetchedSummary - Not-Initiated status detected - this will NOT trigger continuous polling");
    }

    // Log status distribution for better monitoring
    const statusDistribution = fetchTableData?.reduce((acc, item) => {
      const status = (item?.STATUS || item?.status) === null ? 'IN-PROGRESS' : ((item?.STATUS || item?.status)?.toUpperCase() || 'UNKNOWN');
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});
    console.log("📈 FetchedSummary - Status distribution:", statusDistribution);

    if (fetchTableData?.length > 0 && inProcessStatusCount > 0) {
      console.log("⏱️ FetchedSummary - Starting interval polling for in-process files...");
      console.log("🔄 FetchedSummary - Interval will run every 10 seconds until all files are processed");

      let intervalCount = 0;

      intervalRef.current = setInterval(async () => {
        intervalCount++;
        console.log(`\n🔄 FetchedSummary - Interval execution #${intervalCount} - ${new Date().toLocaleTimeString()}`);

        try {
          // Fetch updated data using checkbox-selected values
          console.log("📡 FetchedSummary - Fetching updated data...");
          
          // Extract GSTINs and return period from selected rows for API call
          const selectedGstinsFromRows = selectedRowFetch.map(row => row.GSTIN || row.gstin).filter(Boolean);
          const selectedReturnPeriodFromRows = selectedRowFetch.map(row => row.FISCAL_YEAR || row.fiscal_year).filter(Boolean)[0] || selectedReturnPeriod;
          
          const updatedData = await dispatch(
            getFetchSubmitData(
              selectedGstinsFromRows.length > 0 ? selectedGstinsFromRows : selectedGstinList,
              selectedReturnPeriodFromRows,
              false
            )
          );

          console.log("✅ FetchedSummary - Updated data fetch completed:", {
            totalRecords: updatedData?.length,
            intervalExecution: intervalCount
          });

          // Count in-process files
          const previousCount = inProcessStatusCount;
          inProcessStatusCount = countInProcessFiles(updatedData);

          console.log("📊 FetchedSummary - Updated in-process files count:", {
            current: inProcessStatusCount,
            previous: previousCount,
            change: previousCount - inProcessStatusCount
          });

          // Log updated status distribution
          const updatedStatusDistribution = updatedData?.reduce((acc, item) => {
            const status = (item?.STATUS || item?.status) === null ? 'IN-PROGRESS' : ((item?.STATUS || item?.status)?.toUpperCase() || 'UNKNOWN');
            acc[status] = (acc[status] || 0) + 1;
            return acc;
          }, {});
          console.log("📈 FetchedSummary - Updated status distribution:", updatedStatusDistribution);

          // Clear interval if no files are in process
          if (inProcessStatusCount === 0) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
            console.log("🎉 FetchedSummary - All files processed! Interval cleared after", intervalCount, "executions");
            console.log("⏹️ FetchedSummary - Final status distribution:", updatedStatusDistribution);
          } else {
            console.log(`⏳ FetchedSummary - Still ${inProcessStatusCount} files in process. Continuing to poll...`);
          }

        } catch (intervalError) {
          console.error("❌ FetchedSummary - Error during interval execution:", {
            intervalCount,
            error: intervalError,
            message: intervalError.message
          });
        }

      }, 10000);

    } else if (fetchTableData?.length === 0) {
      console.log("ℹ️ FetchedSummary - No data available - skipping interval setup");
    } else {
      console.log("✅ FetchedSummary - All files already processed - no interval needed");
    }
  };

  // Effect to manage auto-refresh based on data changes (following Fetch pattern)
  useEffect(() => {
    if (fetchTableData && fetchTableData.length > 0) {
      startAutoRefresh();
    }

    // Cleanup interval on unmount or dependency change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        console.log("🧹 FetchedSummary - Interval cleaned up");
      }
    };
  }, [fetchTableData, selectedGstinList, selectedReturnPeriod, selectedRowFetch, dispatch]);

  // Clear selected states and cleanup on component unmount
  useEffect(() => {
    return () => {
      dispatch(setSelectedFetch([]));
      dispatch(setSelectedRowFetch([]));
      
      // Clean up interval and timeout on unmount
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        console.log("🧹 FetchedSummary - Component unmounting - interval cleared");
      }
      if (loaderTimeoutRef.current) {
        clearTimeout(loaderTimeoutRef.current);
        loaderTimeoutRef.current = null;
      }
    };
  }, [dispatch]);
  const handleOnSelect = (row, isSelect) => {
    if (isSelect) {
      dispatch(setSelectedFetch([...selectedFetch, row.uniqueId || row.FILE_ID]));
      dispatch(setSelectedRowFetch([...selectedRowFetch, row]));
    } else {
      const updatedSelection = selectedFetch.filter((id) => id !== (row.uniqueId || row.FILE_ID));
      dispatch(setSelectedFetch(updatedSelection));

      const updatedRows = selectedRowFetch.filter((r) => (r.uniqueId || r.FILE_ID) !== (row.uniqueId || row.FILE_ID));
      dispatch(setSelectedRowFetch(updatedRows));
    }
  };

  const handleOnSelectAll = (isSelect, rows) => {
    const ids = rows.map((r) => r.uniqueId || r.FILE_ID);
    if (isSelect) {
      dispatch(setSelectedFetch(ids));
      dispatch(setSelectedRowFetch(rows));
    } else {
      dispatch(setSelectedFetch([]));
      dispatch(setSelectedRowFetch([]));
    }
  };

  // Checkbox row selection configuration
  const selectRow = {
    mode: "checkbox",
    selected: selectedFetch,
    onSelect: handleOnSelect,
    onSelectAll: handleOnSelectAll,
    headerColumnStyle: {
      width: "3%",
      verticalAlign: "text-top",
    },
    selectColumnStyle: {
      width: "3%",
      textAlign: "center",
    },
  };

  // Clear selected states on component unmount
  useEffect(() => {
    return () => {
      dispatch(setSelectedFetch([]));
      dispatch(setSelectedRowFetch([]));
    };
  }, [dispatch]);

  // Check if any selected row has non-downloadable status - only COMPLETED status is downloadable
  const hasNonDownloadableRows = selectedRowFetch?.some((row) => {
    const status = row?.STATUS || row?.status;
    return status?.toUpperCase() !== "COMPLETED";
  });

  // Determine if download should be disabled
  const isDownloadDisabled = selectedFetch?.length === 0 || hasNonDownloadableRows;

  // Handle button actions
  const handleFetchGSTR9CSummary = async () => {
    console.log(
      "🚀 Fetch GSTR-9C Summary clicked - clearing interval and starting fresh"
    );
    if (validateRequiredFields()) {
      return errorToastMessage(COMMON_MSGS.ENTER_MANDATORY_FIELDS);
    }

    // Clear any existing interval before starting fetch process
    if (clearAndRestartInterval) {
      clearAndRestartInterval(true); // forceClear = true
    }

    // Extract GSTINs and Return Period from checkbox-selected rows instead of dropdown values
    const selectedGstinsFromRows = selectedRowFetch.map(row => row.GSTIN || row.gstin).filter(Boolean);
    const selectedReturnPeriodFromRows = selectedRowFetch.map(row => row.FISCAL_YEAR || row.fiscal_year).filter(Boolean)[0] || selectedReturnPeriod;
    
    console.log("🔍 FetchedSummary - Selected data comparison:", {
      dropdownGstins: selectedGstinList,
      checkboxSelectedGstins: selectedGstinsFromRows,
      dropdownReturnPeriod: selectedReturnPeriod,
      checkboxSelectedReturnPeriod: selectedReturnPeriodFromRows,
      selectedRowsCount: selectedRowFetch.length,
      selectedRows: selectedRowFetch
    });

    // Execute fetch summary and data logic with checkbox-selected values
    await dispatch(fetchSummary(selectedGstinsFromRows, selectedReturnPeriodFromRows));
    await dispatch(
      getFetchSubmitData(selectedGstinList, selectedReturnPeriod, false)
    );
      dispatch(setSelectedFetch([]));
      dispatch(setSelectedRowFetch([]));

    console.log(
      "✅ Fetch completed, checking status before refreshing data"
    );
    
    // Check if any records have "Not-Initiated" status
    // If so, do not refresh data to avoid unnecessary API calls
    const hasNotInitiatedStatus = fetchTableData?.some(row => {
      const status = row?.STATUS || row?.status;
      return status === "Not-Initiated";
    });
    
    if (!hasNotInitiatedStatus) {
      console.log("No 'Not-Initiated' status found, refreshing data and starting new interval");
      // Refresh data using parent's getFetchData function - this will start a new interval
      if (getFetchData) {
        getFetchData(false); // false to avoid toast notification
      }
    } else {
      console.log("'Not-Initiated' status detected, skipping data refresh to avoid unnecessary API calls");
    }
  };

  const handleDownload = async () => {
    if (validateRequiredFields()) {
      return errorToastMessage(COMMON_MSGS.ENTER_MANDATORY_FIELDS);
    }

    if (selectedFetch?.length === 0) {
      return errorToastMessage("Please select at least one row to download");
    }

    await dispatch(downloadFetchReport(selectedRowFetch));
    dispatch(setSelectedFetch([]));
    dispatch(setSelectedRowFetch([]));
  };

  const statusFormatter = (cell, row) => {
    const status = row?.STATUS;
    const upperStatus = status?.toUpperCase();

    // Determine background color based on status
    let bgColor = "gray"; // Default gray (for unknown or neutral statuses)

    if (upperStatus === "COMPLETED")
      bgColor = "green"; // Green (success/completed)
    else if (status === null)
      bgColor = "orange"; // Yellow (in-progress/null status)
    else if (upperStatus === "FAILED") bgColor = "red"; // Red (failure/error)

    const showText = status === null ? "Pending" : status;

    return (
      <div
        className="circle-div"
        style={{
          cursor: upperStatus === "FAILED" ? "pointer" : "default",
          color:bgColor,
        }}
      >
        {showText}
        {/* <div
          className="circle-span"
          style={{
            marginTop: "3px",
            marginLeft: "8px",
            backgroundColor: bgColor,
            cursor: upperStatus === "FAILED" ? "pointer" : "default",
          }}
        ></div> */}
      </div>
    );
  };

  // Table columns configuration
  const columns = [
    {
      dataField: "GSTIN",
      text: "GSTIN",
      headerStyle: {
        backgroundColor: "#4f2d7f",
        color: "#fff",
        fontFamily: "GT-Walsheim-medium",
        fontSize: "12px",
        fontWeight: "bold",
        textAlign: "center",
        width: "32.33%",
      },
      style: {
        fontFamily: "GT-Walsheim-medium",
        fontSize: "11px",
        textAlign: "center",
        fontWeight: "bold",
      },
    },
    {
      dataField: "CREATED_DATE",
      text: "Date & Time",
      headerStyle: {
        backgroundColor: "#4f2d7f",
        color: "#fff",
        fontFamily: "GT-Walsheim-medium",
        fontSize: "12px",
        fontWeight: "bold",
        textAlign: "center",
        width: "32.33%",
      },
      style: {
        fontFamily: "GT-Walsheim-medium",
        fontSize: "11px",
        textAlign: "center",
      },
      formatter: (cell) => {
        return cell ? moment(cell).format("DD/MM/YYYY HH:mm:ss") : "";
      },
    },
    {
      dataField: "fetchStatus",
      text: "Status",
      headerStyle: {
        backgroundColor: "#4f2d7f",
        color: "#fff",
        fontFamily: "GT-Walsheim-medium",
        fontSize: "12px",
        fontWeight: "bold",
        textAlign: "center",
        width: "32.33%",
      },
      formatter: statusFormatter,
      style: {
        fontFamily: "GT-Walsheim-medium",
        fontSize: "11px",
        textAlign: "center",
      },
    },
  ];



  return (
    <div style={{ padding: "20px" }}>
      {/* Action Buttons Section */}
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: "20px",
          gap: "10px",
        }}
      >
        <Tooltip
          title={
            selectedFetch?.length === 0
              ? "Please select rows to fetch summary"
              : "Fetch GSTR-9C Summary for selected data"
          }
          placement="top"
        >
          <span>
            <Button
              onClick={handleFetchGSTR9CSummary}
              variant="contained"
              color="primary"
              disabled={loaderStatus || validateRequiredFields() || selectedFetch?.length === 0}
              style={{
                backgroundColor: (validateRequiredFields() || selectedFetch?.length === 0) ? "#ccc" : "#4f2d7f",
                color: "white",
                height: "35px",
                paddingLeft: "20px",
                paddingRight: "20px",
                textTransform: "capitalize",
                fontFamily: "GT-Walsheim-medium",
                fontSize: "14px",
                opacity: loaderStatus ? 0.6 : 1,
              }}
            >
              Fetch GSTR-9C Summary{" "}
              {selectedFetch?.length > 0 ? `(${selectedFetch.length})` : ""}
            </Button>
          </span>
        </Tooltip>
        <Tooltip
          title={
            selectedFetch?.length === 0
              ? "Please select rows to download"
              : hasNonDownloadableRows
              ? "Only records with COMPLETED status can be downloaded"
              : "Download selected data"
          }
          placement="top"
        >
          <span>
            <Button
              onClick={handleDownload}
              variant="contained"
              color="primary"
              startIcon={<DownloadIcon />}
              disabled={loaderStatus || isDownloadDisabled}
              style={{
                backgroundColor: isDownloadDisabled ? "#ccc" : "#4f2d7f",
                color: "white",
                height: "35px",
                paddingLeft: "20px",
                paddingRight: "20px",
                textTransform: "capitalize",
                fontFamily: "GT-Walsheim-medium",
                fontSize: "14px",
                opacity: loaderStatus ? 0.6 : 1,
              }}
            >
              Download{" "}
              {selectedFetch?.length > 0 ? `(${selectedFetch.length})` : ""}
            </Button>
          </span>
        </Tooltip>
      </div>

      {/* Bootstrap Table Section */}
      {fetchTableData && fetchTableData.length > 0 ? (
        <>
          <div style={{ marginTop: "20px" }}>
            <BootstrapTable
              keyField="uniqueId"
              data={fetchTableData || []}
              columns={columns}
              //   pagination={paginationFactory(paginationOptions)}
              selectRow={selectRow}
              loading={showLoader || loaderStatus}
              noDataIndication={() => (
                <div
                  style={{
                    textAlign: "center",
                    padding: "50px",
                    fontFamily: "GT-Walsheim-medium",
                    fontSize: "16px",
                    color: "#666",
                  }}
                >
                  No fetched summary data available
                </div>
              )}
              rowStyle={{
                backgroundColor: "#fff",
              }}
              striped
              hover
            />
          </div>
        </>
      ) : (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              padding: '50px 20px',
              fontFamily: 'GT-Walsheim-medium',
              fontSize: '16px',
              color: '#666',
              minHeight: '200px',
              width: '100%',
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
            }}
          >
            No fetched summary data available
          </div>
        </>
      )}
    </div>
  );
};

export default FetchedSummary;
